/* eslint max-lines: ["error", {max: 413}] -- slightly longer than typical max lines */
/* eslint max-lines-per-function: ["error", {max: 295}] -- this applies to the entire file */
/* eslint max-statements: ["error", {max: 48}] -- complex modal / lots of statements */
/* eslint complexity: ["error", {max: 23}] -- applies to entire file */
import { useEffect, useMemo, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
  ProfileAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ModalTestID, ProfileTestID } from "@/constants/TestID";
import { router, useLocalSearchParams } from "@/crossPlatform/expo-router";
import { useModalTracker } from "@/hooks/useModalTracker";
import { useToast } from "@/hooks/useToast";
import type { MixpanelProps } from "@/services/Analytics";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import {
  useGetFullProfileQuery,
  useUpdateLinkedPatientProfileMutation,
  useUpdateSupportUserProfileMutation,
} from "@/store/api/user";
import {
  useAppUserProxyPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import {
  BasicInfoSection,
  ContactInfoSection,
  OtherInfoSection,
} from "@/ui/components/profile";
import ModalFooter from "@/ui/composed/modal/ModalFooter";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { ActionBar, FAIconName, FullScreenSpinner } from "@/ui/core";
import { VStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { getChanges } from "@/utils/analytics.util";
import { getPermissionsForUser } from "@/utils/profile/permissions.util";
import { joinTestIDs } from "@/utils/string.util";
import {
  getBasicInfo,
  getContactInfo,
  getOtherInfo,
  getPreferredFirstName,
} from "@/utils/user.util";

const baseTestID = ProfileTestID.EditProfile;

const getRelationToPatient = ({
  isAppUser,
  isAppUserSupport,
  proxyPatients,
  unmodifiedProfile,
}: {
  isAppUser: boolean;
  isAppUserSupport: boolean;
  proxyPatients?: LinkedPatientProfile[];
  unmodifiedProfile: FullProfileResponse;
}) => {
  if (!isAppUserSupport) {
    return undefined;
  }

  if (!isAppUser) {
    return unmodifiedProfile.relationToPatient;
  }

  if (isNil(proxyPatients)) {
    return undefined;
  }

  return proxyPatients
    .map((p: LinkedPatientProfile) => {
      const firstName = getPreferredFirstName({
        chosenName: p.chosenName,
        firstName: p.firstName,
      });
      const role = p.relationToPatient;
      return `${role} to ${firstName}`;
    })
    .join(",\n");
};

const getChangesForMixpanel = ({
  payload,
  unmodifiedAddress,
  unmodifiedProfile,
}: {
  payload: UpdateProfileRequest;
  unmodifiedAddress?: GooglePlacesAddress;
  unmodifiedProfile: FullProfileResponse;
}) => {
  const previous: MixpanelProps = {};
  // keep all keys of unmodifiedProfile to compare against profile
  Object.keys(unmodifiedProfile).forEach((key: string) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
    previous[key] = unmodifiedProfile[key as keyof FullProfileResponse];
  });

  // add unmodifiedProfile.address keys as top-level keys, to be comparable to the payload that does so
  if (!isNil(unmodifiedAddress)) {
    Object.keys(unmodifiedAddress).forEach((key: string) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
      previous[key] = unmodifiedAddress[key as keyof GooglePlacesAddress];
    });
  }

  return getChanges({ current: payload, previous });
};

const Modal = () => {
  const { setActiveModal } = useModalTracker();
  useEffect(() => {
    setActiveModal(ModalPath.EditProfile);
    return () => setActiveModal(null);
  }, [setActiveModal]);

  const { track, trackPageView } = useAnalytics();
  const { captureException } = useAppMonitor();
  useEffect(() => {
    trackPageView(ProfileAnalyticsPageView.EditProfile);
  }, [trackPageView]);
  const { showToast } = useToast();
  const showSuccessToast = () =>
    showToast({
      text: "Changes successfully applied",
      variant: "success",
    });
  const showErrorToast = () =>
    showToast({
      text: "Hmm something went wrong. Please try again",
      variant: "error",
    });
  const { externalId: appUserId, isSupport: isAppUserSupport } =
    useAppUserSelector();
  const { selectedUserId } = useLocalSearchParams<{
    selectedUserId: string;
  }>();
  const isAppUser = selectedUserId === appUserId;
  const {
    data: fullProfileData,
    error: fullProfileError,
    isError: isFullProfileError,
    isFetching: isFullProfileFetching,
    isLoading: isFullProfileLoading,
  } = useGetFullProfileQuery();
  const proxyPatients = useAppUserProxyPatientsSelector();
  const unmodifiedProfile = isAppUser
    ? fullProfileData
    : proxyPatients?.find((p) => p.externalId === selectedUserId);
  const [updateSupportUserProfile, { isLoading: isAppUserLoading }] =
    useUpdateSupportUserProfileMutation();
  const [updateLinkedPatientProfile, { isLoading: isLinkedPatientLoading }] =
    useUpdateLinkedPatientProfileMutation();
  const [payloadBasicInfo, setPayloadBasicInfo] = useState<
    undefined | UserBasicInfo
  >(undefined);
  const [payloadContactInfo, setPayloadContactInfo] = useState<
    undefined | UserContactInfo
  >(undefined);
  const [payloadOtherInfo, setPayloadOtherInfo] = useState<
    undefined | UserOtherInfo
  >(undefined);

  useEffect(() => {
    if (isNil(unmodifiedProfile)) return;
    setPayloadBasicInfo(getBasicInfo(unmodifiedProfile));
    setPayloadContactInfo(getContactInfo(unmodifiedProfile));
    setPayloadOtherInfo(getOtherInfo(unmodifiedProfile));
  }, [unmodifiedProfile]);
  const userPermissions = useMemo(() => {
    return getPermissionsForUser({
      dateOfBirth: unmodifiedProfile?.dateOfBirth,
      isPatient: unmodifiedProfile?.isPatient,
      isProxy: unmodifiedProfile?.isProxy,
    });
  }, [unmodifiedProfile]);

  // if have no data, a spinner is displayed
  // if data is re-fetching, we also display a spinner so that the profile is not edited on stale/outdated data
  if (isFullProfileFetching || isFullProfileLoading) {
    return <FullScreenSpinner />;
  }
  // cannot display edit profile if error getting full profile info
  const errorToastText =
    "There was an issue trying to retrieve this profile's full data";
  if (isFullProfileError || !isNil(fullProfileError)) {
    captureException(fullProfileError);
    showToast({ text: errorToastText, variant: "error" });
    router.dismiss();
    return <FullScreenSpinner />;
  }

  if (isNil(unmodifiedProfile)) {
    const appUserError =
      "Fetched app user's full profile info from API but received empty data";
    const selectedUserError = `Selected a patient ID ${selectedUserId} that does not exist in proxyPatients for appUser ${appUserId}`;
    const message = isAppUser ? appUserError : selectedUserError;
    captureException(new Error(message));
    showToast({ text: errorToastText, variant: "error" });
    router.dismiss();
    return <FullScreenSpinner />;
  }
  if (
    // this can NOT be grouped with the check for isNil(unmodifiedProfile) since that does a router.dismiss()
    // these values will be nil initially because the useEffect above that populates them is only executed
    // after rendering this modal entirely first; if this was grouped above edit-profile would *always* error
    isNil(payloadBasicInfo) ||
    isNil(payloadContactInfo) ||
    isNil(payloadOtherInfo)
  ) {
    // subsequent code depends on these being populated and non-null/non-undefined
    // so if this is still being populated, render a spinner
    // we know this will populate successfully as we've checked isNil(unmodifiedProfile) above already
    return <FullScreenSpinner />;
  }

  const unmodifiedAddress = unmodifiedProfile.address;
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const supports = (unmodifiedProfile as FullProfileResponse).supportedBy?.map(
    (s: SupportProfile) => {
      const firstName = getPreferredFirstName({
        chosenName: s.chosenName,
        firstName: s.firstName,
      });
      const role = s.relationToPatient?.toLowerCase();
      return `${firstName} (${role})`;
    },
  );
  const patientName = isAppUser
    ? undefined
    : getPreferredFirstName({
        chosenName: unmodifiedProfile.chosenName,
        firstName: unmodifiedProfile.firstName,
      });

  const isLoading = isAppUserLoading || isLinkedPatientLoading;

  const handleModalDismiss = () => {
    track(ProfileAnalyticsEvent.DismissEditProfile, {
      [AnalyticsFieldName.Component]: ModalPath.EditProfile,
      [AnalyticsFieldName.Source]: ModalPath.EditProfile,
    });
    router.dismiss();
  };

  const handleCancelPress = () => {
    track(ProfileAnalyticsEvent.CancelEditProfile, {
      [AnalyticsFieldName.Component]: ModalPath.EditProfile,
      [AnalyticsFieldName.Source]: ModalPath.EditProfile,
    });
    router.dismiss();
  };

  const relationToPatient = getRelationToPatient({
    isAppUser,
    isAppUserSupport,
    proxyPatients,
    unmodifiedProfile,
  });

  const handleSaveChanges = () => {
    // user permissions for viewability/edit-ability of each field is handled by
    // each respective component in ContactInfoSection / BasicInfoSection / OtherInfoSection

    // ultimately a full payload with all expected fields must be constructed here for the PUT request
    // and non-editable fields will simply retain their previous values
    const payload: UpdateProfileRequest = {
      addressLine1: payloadContactInfo.addressLine1,
      addressLine2: payloadContactInfo.addressLine2,
      affiliationName: unmodifiedAddress?.affiliationName,
      cellPhone: payloadContactInfo.cellPhone,
      chosenName: payloadBasicInfo.chosenName,
      city: payloadContactInfo.city,
      contactPreferences: payloadContactInfo.contactPreferences,
      country: unmodifiedAddress?.country,
      languages: payloadOtherInfo.languages,
      latitude: unmodifiedAddress?.latitude,
      longitude: unmodifiedAddress?.longitude,
      state: payloadContactInfo.state,
      timezone: unmodifiedProfile.timezone,
      zip: payloadContactInfo.zip,
      // slightly different PUT requests for patient vs non-patient profiles
      ...(unmodifiedProfile.isPatient
        ? { dateOfBirth: payloadOtherInfo.dateOfBirth }
        : {
            gender: payloadBasicInfo.gender,
            pronouns: payloadBasicInfo.pronouns,
          }),
    };
    track(ProfileAnalyticsEvent.SaveChangesEditProfile, {
      [AnalyticsFieldName.Component]: ModalPath.EditProfile,
      [AnalyticsFieldName.Source]: ModalPath.EditProfile,
      ...getChangesForMixpanel({
        payload,
        unmodifiedAddress,
        unmodifiedProfile,
      }),
    });

    if (isAppUser && isAppUserSupport) {
      updateSupportUserProfile(payload)
        .unwrap()
        .then(() => {
          showSuccessToast();
          router.dismiss();
        })
        .catch(() => {
          showErrorToast();
        });
      return;
    }
    updateLinkedPatientProfile({
      body: payload,
      patientExternalId: selectedUserId,
    })
      .unwrap()
      .then(() => {
        showSuccessToast();
        router.dismiss();
      })
      .catch(() => {
        showErrorToast();
      });
  };

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFooter={() => {
        return (
          <ModalFooter
            isLoading={isLoading}
            leftButtonType="cancel"
            onLeftButtonPress={handleCancelPress}
            onRightButtonPress={handleSaveChanges}
            rightButtonLabel="Save changes"
            rightButtonLeftIcon={FAIconName.Check}
            testID={baseTestID}
          />
        );
      }}
      renderHeader={() => {
        return (
          <ModalTitleHeader
            navigationType="close"
            onDismiss={handleModalDismiss}
            testID={joinTestIDs(
              ModalTestID.TitleHeader,
              ModalTestID.EditProfile,
            )}
            title="Edit profile"
          />
        );
      }}
    >
      <VStack gap={36} paddingBottom={30} paddingTop={24}>
        <ActionBar
          iconName={FAIconName.InfoCircle}
          message="Please note, locked fields can only be changed by a member of the Equip team."
          size="small"
          testID={baseTestID}
          variant="warning"
        />
        <BasicInfoSection
          // BasicInfoSection takes the unmodified values to use as its initial state
          // and then manages any changes to that initial state internally
          chosenName={unmodifiedProfile.chosenName}
          firstName={unmodifiedProfile.firstName}
          gender={unmodifiedProfile.gender}
          lastName={unmodifiedProfile.lastName}
          onChange={setPayloadBasicInfo}
          pronouns={unmodifiedProfile.pronouns ?? []}
          userPermissions={userPermissions}
        />
        <ContactInfoSection
          // ContactInfoSection takes the unmodified values to use as its initial state
          // and then manages any changes to that initial state internally
          addressLine1={unmodifiedAddress?.addressLine1}
          addressLine2={unmodifiedAddress?.addressLine2}
          cellPhone={unmodifiedProfile.cellPhone}
          city={unmodifiedAddress?.city}
          contactPreferences={unmodifiedProfile.contactPreferences ?? []}
          email={unmodifiedProfile.email}
          onChange={setPayloadContactInfo}
          state={unmodifiedAddress?.state}
          userPermissions={userPermissions}
          zip={unmodifiedAddress?.zip}
        />
        <OtherInfoSection
          // OtherInfoSection takes the unmodified values to use as its initial state
          // and then manages any changes to that initial state internally
          dateOfBirth={unmodifiedProfile.dateOfBirth}
          isAppUserSupport={isAppUserSupport}
          languages={unmodifiedProfile.languages ?? []}
          onChange={setPayloadOtherInfo}
          patientName={patientName}
          relationToPatient={relationToPatient}
          supports={supports}
          userPermissions={userPermissions}
        />
      </VStack>
    </ModalLayout>
  );
};

// eslint-disable-next-line import-x/no-unused-modules -- Modal/Tab/TabLayout/Screen/Home get used in expo routing but in a way that TypeScript can not infer
export default Modal;
